import { Button, XIcon } from '@tovala/component-library'
import { lazy, Suspense, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { onlineManager } from '@tanstack/react-query'

import { page } from 'utils/analytics'
import { syncCIOInAppRoute } from 'utils/customerio'

import Routes from 'Routes'
import ErrorDisplay from 'components/common/ErrorDisplay'
import { setNavigation } from 'utils/navigationService'

const ReactQueryDevtoolsProduction = lazy(() =>
  import('@tanstack/react-query-devtools/build/modern/production.js').then(
    (d) => ({
      default: d.ReactQueryDevtools,
    }),
  ),
)

const App = () => {
  const [showDevtools, setShowDevtools] = useState(false)

  const location = useLocation()
  const navigate = useNavigate()

  useEffect(() => {
    // eslint-disable-next-line
    // @ts-ignore
    window.toggleDevtools = () => setShowDevtools((old) => !old)
  }, [])

  useEffect(() => {
    page()
    syncCIOInAppRoute()
  }, [location.pathname])

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [location.pathname])

  useEffect(() => {
    setNavigation(navigate)
  }, [navigate])

  return (
    <div className="bg-grey-0">
      <main>
        <Routes />
      </main>

      {/* See "Additional Context" on https://tovala.atlassian.net/browse/WAT-431. The tl;dr is that
      Chrome seems to sometimes think the user is not online when they actually are (or at least the
      navigator.onLine property is not set properly.) */}
      <OfflineWarning />

      {showDevtools && (
        <Suspense fallback={null}>
          <ReactQueryDevtoolsProduction />
        </Suspense>
      )}
    </div>
  )
}

export default App

const OfflineWarning = () => {
  const [showWarning, setShowWarning] = useState(!onlineManager.isOnline())

  useEffect(() => {
    return onlineManager.subscribe((isOnline) => {
      setShowWarning(!isOnline)
    })
  }, [])

  if (showWarning) {
    return (
      <div className="fixed right-16 top-24 z-40 max-w-sm md:bottom-0 md:left-0 md:right-0 md:top-auto md:m-1 md:max-w-none">
        <div className="absolute right-6 top-6 h-6 w-6">
          <Button
            buttonStyle="link"
            onClick={() => setShowWarning(false)}
            size="fluid"
          >
            <XIcon />
          </Button>
        </div>
        <ErrorDisplay
          display="page"
          helpToFix="Please check your network connection."
          wayOut="Toggling your network connection may help."
          whatHappened="Offline"
          why="We cannot process any of your requests because you may be offline."
        />
      </div>
    )
  }

  return null
}
